import type { JSONContent } from "@tiptap/core"
import {
  getMentionResources,
  type MentionResource,
} from "./media/resourceMentions"
import type { UploadedResource } from "./media/uploadedResources"
import { splitPromptUnderline } from "./promptUnderline"

/** A template field is editable text, not an atomic resource mention. */
export type PromptField = {
  name: string
  label: string
  required: boolean
  maxLength: number
}

export type MentionNodeAttrs = {
  id: string
  kind: "image" | "audio" | "video"
  label: string
  missing: boolean
  name: string
  type: string
  url: string
}

export const toMentionAttrs = (
  resource: MentionResource,
): MentionNodeAttrs => ({
  id: resource.mentionId,
  kind: resource.kind,
  label: resource.label,
  missing: false,
  name: resource.name || resource.label,
  type: resource.type || resource.kind,
  url: resource.url,
})

const textContent = (
  value: string,
  readLine: (line: string) => JSONContent[],
): JSONContent[] => {
  const content: JSONContent[] = []
  value.split("\n").forEach((line, index) => {
    if (index) content.push({ type: "hardBreak" })
    content.push(...readLine(line))
  })
  return content
}

export const buildEditorContent = ({
  mentionableResources,
  value,
  fields = [],
}: {
  mentionableResources: UploadedResource[]
  value: string
  fields?: (PromptField & { value: string })[]
}): JSONContent => {
  const mentions = new Map(
    getMentionResources(mentionableResources).map((resource) => [
      `@${resource.label}`,
      resource,
    ]),
  )
  const fieldMap = new Map(fields.map((field) => [field.name, field]))
  const readLine = (line: string, expandFields = true): JSONContent[] => {
    const content: JSONContent[] = []
    let end = 0
    for (const match of line.matchAll(/{{([^{}]+)}}|@(图片|视频|音频)\d+/g)) {
      const offset = match.index
      if (offset > end)
        content.push({ type: "text", text: line.slice(end, offset) })
      const field = expandFields ? fieldMap.get(match[1]) : undefined
      if (match[1]) {
        if (field) {
          const { value: fieldValue, ...attrs } = field
          content.push({
            type: "promptField",
            attrs,
            content: textContent(fieldValue, (text) => readLine(text, false)),
          })
        } else {
          content.push({ type: "text", text: match[0] })
        }
      } else {
        const resource = mentions.get(match[0])
        const label = match[0].slice(1)
        const kind = label.startsWith("图片")
          ? "image"
          : label.startsWith("视频")
            ? "video"
            : "audio"
        content.push({
          type: "resourceMention",
          attrs: resource
            ? toMentionAttrs(resource)
            : ({
                id: `missing:${label}`,
                kind,
                label,
                missing: true,
                name: label,
                type: kind,
                url: "",
              } satisfies MentionNodeAttrs),
        })
      }
      end = offset + match[0].length
    }
    if (end < line.length) content.push({ type: "text", text: line.slice(end) })
    return content
  }
  // Staff 用 <u>…</u> 标记的片段转为下划线 mark；标记本身不进入文档文本
  const paragraphs: JSONContent[][] = [[]]
  for (const segment of splitPromptUnderline(value)) {
    segment.text.split("\n").forEach((line, index) => {
      if (index) paragraphs.push([])
      const content = readLine(line)
      paragraphs.at(-1)!.push(
        ...(segment.underline
          ? content.map((node) => ({
              ...node,
              marks: [{ type: "underline" }],
            }))
          : content),
      )
    })
  }
  return {
    type: "doc",
    content: paragraphs.map((content) => ({ type: "paragraph", content })),
  }
}

export const getPromptNodeText = (node: JSONContent): string => {
  if (node.type === "text") return node.text || ""
  if (node.type === "hardBreak") return "\n"
  if (node.type === "resourceMention") return `@${node.attrs?.label || ""}`
  return (node.content || [])
    .map(getPromptNodeText)
    .join(node.type === "doc" ? "\n" : "")
}

export const visitPromptNodes = (
  node: JSONContent,
  visit: (node: JSONContent) => void,
): void => {
  visit(node)
  node.content?.forEach((child) => visitPromptNodes(child, visit))
}

export const getPromptDocumentError = (document: JSONContent): string => {
  let error = ""
  visitPromptNodes(document, (node) => {
    if (error) return
    if (node.type === "resourceMention" && node.attrs?.missing) {
      error = "请替换或删除提示词中的失效素材引用"
    }
    if (node.type !== "promptField") return
    const text = getPromptNodeText(node).trim()
    if (node.attrs?.required && !text) {
      error = `请填写${node.attrs.label}`
    } else if (
      node.attrs?.maxLength > 0 &&
      text.length > node.attrs.maxLength
    ) {
      error = `${node.attrs.label}不能超过${node.attrs.maxLength}字`
    }
  })
  if (error) return error
  const text = getPromptNodeText(document)
  if (!text.trim()) return "请输入提示词"
  if (/{{[^{}]+}}/.test(text)) return "请填写提示词中的待替换内容"
  return ""
}
